import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * Brands tab (spec 015 / Addendum B5; approval lifecycle: spec 017 Feature D;
 * UI/UX feedback item 9) — manage the workspace's brand identities (name, primary +
 * secondary colors, logo). One is the default applied at generation. The embossed
 * brand logo is mandatory on outputs (enforced by the render/customization layer).
 *
 * Approval lifecycle (Feature D): new brands start as drafts; the customer submits
 * them for admin approval. The NAME is fixed at creation (the backend rejects a
 * rename with 422), so only colors + logo are editable afterward. There is no delete
 * (the backend returns 405). Rejected brands surface their `rejection_reason`;
 * pending brands surface when they were `submitted_at`.
 */
import { confirmAction } from "../ui/confirmAction.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import { useServices } from "../app/context.js";
import { serverTsDate } from "@genz/platform";
import type { BrandProfile, BrandStatus } from "@genz/platform";
import {
  BRAND_STATUS_BADGE, BRAND_STATUS_FILTERS, BRAND_STATUS_LABEL, brandFilterLabel,
  type BrandStatusFilter,
} from "./brandStatus.js";
import { MIME_EXT } from "../options/labels.js";
import { isDurableRef } from "../app/refs.js";
import { BrandSwatch } from "./BrandSwatch.js";
import { toast } from "../ui/Toast.js";
import "./branding.css";
import { fetchWithTimeout, UPLOAD_TIMEOUT_MS } from "../templates/timeout.js";
import { RowAction } from "../ui/RowAction.js";

// WS-B B-10 / MIN-7: ONE brand-status vocabulary — the labels, the tones and the FILTER
// ORDER. These two pages each kept a private copy, and their orders had drifted.
const STATUS_BADGE = BRAND_STATUS_BADGE;
const STATUS_LABEL = BRAND_STATUS_LABEL;
type StatusFilter = BrandStatusFilter;
const FILTERS = BRAND_STATUS_FILTERS;

/** Records without a status (older brands) are treated as approved. */
function statusOf(b: BrandProfile): BrandStatus {
  return b.status ?? "approved";
}

/** Human date for submitted/reviewed timestamps; "—" when missing/invalid. */
function fmtDate(iso?: string | null): string {
  const d = serverTsDate(iso);
  return d ? d.toLocaleDateString() : "—";
}

/**
 * GEN-6 lives server-side: ANY visual edit (colors or logo) to an APPROVED brand demotes
 * it to Draft, clears `reviewed_by`/`submitted_at` AND clears `is_default`. That is a real
 * cost — an unapproved brand cannot generate — so it must never happen behind the
 * operator's back. Same `window.confirm` guard the rest of the console uses.
 */
async function confirmVisualEdit(brand: BrandProfile): Promise<boolean> {
  if (statusOf(brand) !== "approved") return true;
  return confirmAction(
    `“${brand.name}” is approved. Changing its colors or logo sends it back to Draft ` +
      `(and drops it as the workspace default) until an admin approves it again. Continue?`,
  );
}

/** Per-row color editor (spec 017 FR-022: customers may change the COLORS after creation
 *  — the name stays locked). Edits both primary + secondary; commits via the PATCH. */
function BrandColorEdit({ brand, busy, confirmEdit, onSave }: {
  brand: BrandProfile;
  busy: boolean;
  confirmEdit: () => Promise<boolean>;
  onSave: (input: { color_primary?: string; color_secondary?: string }) => void;
}) {
  const [primary, setPrimary] = useState(brand.color_primary ?? "");
  const [secondary, setSecondary] = useState(brand.color_secondary ?? "");
  /**
   * P0-6: the inputs were seeded at mount only and the row never remounts (`key={b.id}`
   * is stable across a refresh), so after a save they kept the STALE text — an emptied
   * secondary stayed empty while the record still held the old color, leaving the row
   * permanently `dirty`. Resync from the record, but only for the field the SERVER
   * changed, so an edit the operator is mid-typing survives an unrelated refresh.
   */
  const serverSeen = useRef({ primary: brand.color_primary ?? "", secondary: brand.color_secondary ?? "" });
  useEffect(() => {
    const next = { primary: brand.color_primary ?? "", secondary: brand.color_secondary ?? "" };
    const prev = serverSeen.current;
    if (next.primary !== prev.primary) setPrimary(next.primary);
    if (next.secondary !== prev.secondary) setSecondary(next.secondary);
    serverSeen.current = next;
  }, [brand.color_primary, brand.color_secondary]);
  const dirty =
    primary.trim() !== (brand.color_primary ?? "") ||
    secondary.trim() !== (brand.color_secondary ?? "");
  return (
    <span className="brands__coloredit">
      <input
        type="text"
        placeholder="#primary"
        value={primary}
        onChange={(e) => setPrimary(e.target.value)}
        data-testid="brand-color-edit"
      />
      <input
        type="text"
        placeholder="#secondary"
        value={secondary}
        onChange={(e) => setSecondary(e.target.value)}
        data-testid="brand-color2-edit"
      />
      {/* WS-Z9 / F-14: the inline commit of a ROW editor, the same shape CatalogAdmin's row
          editor Save takes — `check`, not the page-commit tier. It is rendered into the row
          by a child component, which is why the sweep could not see it. */}
      <RowAction
        label="Save"
        hint="Save this brand's colours"
        icon="check"
        tone="quiet"
        disabled={busy || !dirty}
        data-testid="brand-color-save"
        onClick={async () => {
          if (!await confirmEdit()) return;
          onSave({
            color_primary: primary.trim(),
            // P0-4: `|| undefined` was dropped by JSON.stringify, so the server never saw
            // the field and the secondary color could NEVER be cleared. "" is the clear
            // signal the server already honours for the primary.
            color_secondary: secondary.trim(),
          });
        }}
      />
    </span>
  );
}

/** Per-row logo control: presign via brandLogoUpload, PUT the bytes, PATCH logo_ref with
 *  the public URL. A failed upload SURFACES an error instead of the old silent data-URL
 *  fallback — a multi-MB base64 blob in the String(255) logo_ref column hard-fails on
 *  Postgres and hid the broken hosting; the cloud rejects non-http(s) refs now (422). */
function BrandLogoEdit({ brand, busy, confirmEdit, onSave }: {
  brand: BrandProfile;
  busy: boolean;
  confirmEdit: () => Promise<boolean>;
  onSave: (logoRef: string) => void;
}) {
  const { client, io, logger } = useServices();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function pick() {
    // Warn BEFORE the file picker: a logo swap demotes an approved brand (see
    // confirmVisualEdit), so the operator should decide before choosing a file.
    if (!await confirmEdit()) return;
    const files = await io.pickFiles({
      accept: ["image/png", "image/jpeg", "image/webp"],
      multiple: false,
      maxBytes: 4 * 1024 * 1024,
    });
    if (!files.length) return;
    const f = files[0];
    const mime = f.mime ?? "image/png";
    const ext = MIME_EXT[mime] ?? "png";
    setUploading(true);
    setUploadError(null);
    try {
      const target = await client.brandLogoUpload(brand.id, ext);
      if (!/^https?:\/\//.test(target.upload_url)) throw new Error("hosting is local-only (no CDN target)");
      const res = await fetchWithTimeout(target.upload_url, {
        method: "PUT",
        headers: { "Content-Type": mime, ...(target.fields ?? {}) },
        body: f.bytes as BlobPart as BodyInit,
      }, UPLOAD_TIMEOUT_MS, "Brand logo upload");
      if (!res.ok) throw new Error(`upload PUT -> ${res.status}`);
      if (!isDurableRef(target.public_url)) throw new Error("hosting returned a non-durable public URL — check hosting_public_host");
      onSave(target.public_url);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      logger?.log("error", `Brands · logo upload failed (${msg}) — the brand keeps its previous logo`);
      setUploadError(`Upload failed: ${msg}`);
    } finally {
      setUploading(false);
    }
  }

  return (
    <span className="brands__logo">
      {brand.logo_ref && (
        <img
          src={brand.logo_ref}
          alt={`${brand.name} logo`}
          className="brands__logo-thumb"
          data-testid="brand-logo"
        />
      )}
      {/* WS-Z9 / F-14: one control, two verbs — and the NAME says which, because the row
          already shows the thumbnail that is the difference. The `…` it used to print while
          uploading is the shared spinner now. */}
      <RowAction
        label={brand.logo_ref ? "Replace logo" : "Upload logo"}
        hint={brand.logo_ref ? "Replace this brand's logo" : "Upload a logo for this brand"}
        icon="upload"
        tone="quiet"
        disabled={busy || uploading}
        busy={uploading}
        data-testid="brand-logo-upload"
        onClick={() => void pick()}
      />
      {uploadError && (
        <span className="error" role="alert" data-testid="brand-logo-error"><ErrorNotice detail={uploadError} /></span>
      )}
    </span>
  );
}

export function BrandsPanel() {
  const { client } = useServices();
  const [brands, setBrands] = useState<BrandProfile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState("");
  const [color2, setColor2] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setBrands(await client.listBrands());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** `done` is what the operator is told when it worked. A mutation whose only feedback is
   *  the list quietly re-rendering is a mutation the operator cannot be sure ran. */
  async function run(fn: () => Promise<unknown>, done?: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
      if (done) toast(done);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    await run(async () => {
      await client.createBrand({
        name: name.trim(),
        color_primary: color.trim() || undefined,
        color_secondary: color2.trim() || undefined,
      });
      setName("");
      setColor("");
      setColor2("");
    }, `${name.trim()} added`);
  }

  // Direction B: a search FIRST, then the facet. A workspace with thirty brands had only a
  // status dropdown to find one with, which is a filter for a state and not for a name.
  const list = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (brands ?? []).filter(
      (b) => (filter === "all" || statusOf(b) === filter) &&
        (!needle || b.name.toLowerCase().includes(needle)),
    );
  }, [brands, filter, search]);

  return (
    <section className="brands" data-testid="brands-panel">
      <header className="panel-head">
        <div>
          <h1 className="panel-title">Brands</h1>
        </div>
      </header>
      {/* P2: this line used to promise "the default brand (and its embossed logo) is applied
          to generated mockups". Nothing applies it — generation takes an explicit brand and
          never falls back to the default, and the colors are identification only. Say what
          these controls actually do today rather than describe a pipeline that isn't wired. */}
      <p className="faint">
        Multiple brand identities under one account. New brands need admin approval before they
        can be used, and any change to a brand's colors or logo sends it back for review. The
        default marks which identity the workspace treats as primary; colors label the brand in
        this list. Both are picked per job at generation — neither is applied automatically.
      </p>
      {error && <p role="alert" data-testid="brands-error"><ErrorNotice detail={error} /></p>}

      <form className="brands__create" onSubmit={create} data-testid="brand-create-form">
        <input
          type="text"
          placeholder="Brand name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="brand-name"
        />
        <input
          type="text"
          placeholder="Primary color (#hex)"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          data-testid="brand-color"
        />
        <input
          type="text"
          placeholder="Secondary color (#hex)"
          value={color2}
          onChange={(e) => setColor2(e.target.value)}
          data-testid="brand-color2"
        />
        <button
          type="submit"
          // A page-level commit, so the base 36px height, not the 30px row size (DESIGN.md's
          // control-height table says `--sm` is "row and toolbar actions only — never a form's
          // submit"). It stays the ONE primary on this page; the row action below gave its
          // volt up so this can keep it (K2-20 / crux B4).
          className="btn btn--primary"
          disabled={busy || !name.trim()}
          data-testid="brand-create"
        >
          Add brand
        </button>
      </form>

      <div className="brands__bar">
        <label className="brands__filter brands__filter--search">
          Search
          <input
            type="search"
            value={search}
            placeholder="Brand name"
            data-testid="brand-search"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="brands__filter">
          Status
          <select
            value={filter}
            disabled={busy}
            data-testid="brand-status-filter"
            onChange={(e) => setFilter(e.target.value as StatusFilter)}
          >
            {FILTERS.map((f) => (
              <option key={f} value={f}>{brandFilterLabel(f)}</option>
            ))}
          </select>
        </label>
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon="brands"
          title={(brands ?? []).length === 0 ? "No brands yet" : "No brands match"}
          hint={(brands ?? []).length === 0
            ? "Add a brand to overlay its logo on your mock-ups."
            : "No brand matches this search and status. Clear them to see the rest."}
          testId="brands-empty"
        />
      ) : (
        <ul className="brands__list" data-testid="brands-list">
          {list.map((b) => {
            const status = statusOf(b);
            return (
              <li key={b.id} className="brands__row" data-testid="brand-row">
                <div className="brands__status">
                  {b.logo_ref && (
                    <img
                      src={b.logo_ref}
                      alt={`${b.name} logo`}
                      className="brands__logo-thumb"
                      data-testid="brand-row-logo"
                    />
                  )}
                  <strong>{b.name}</strong>
                  {b.color_primary && <BrandSwatch color={b.color_primary} />}
                  {b.color_secondary && (
                    <BrandSwatch color={b.color_secondary} testId="brand-swatch-secondary" />
                  )}
                  <span
                    className={`badge ${STATUS_BADGE[status]}`}
                    data-testid="brand-status-badge"
                  >
                    {STATUS_LABEL[status]}
                  </span>
                </div>

                {status === "pending" && (
                  <p className="faint brands__meta" data-testid="brand-submitted-at">
                    Submitted {fmtDate(b.submitted_at)}
                  </p>
                )}
                {status === "rejected" && b.rejection_reason && (
                  <p className="brands__reason" role="status" data-testid="brand-rejection-reason">
                    Rejected: {b.rejection_reason}
                  </p>
                )}

                <div className="brands__actions">
                  <BrandColorEdit
                    brand={b}
                    busy={busy}
                    confirmEdit={() => confirmVisualEdit(b)}
                    onSave={(input) => void run(() => client.updateBrand(b.id, input), `${b.name} — colours saved`)}
                  />
                  <BrandLogoEdit
                    brand={b}
                    busy={busy}
                    confirmEdit={() => confirmVisualEdit(b)}
                    onSave={(logoRef) => void run(() => client.updateBrand(b.id, { logo_ref: logoRef }), `${b.name} — logo saved`)}
                  />
                  {(status === "draft" || status === "rejected") && (
                    <RowAction
                      // ONE row action among four, repeated once per brand — so the quiet
                      // tier. As `--primary` it put a volt fill on every draft row and made
                      // Brands the only surface in the product with two loud invitations in
                      // one viewport (K2-20 / crux B4). Same reasoning K2-2 used to retire
                      // `.btn--success`: a per-row fill is a fill per row.
                      label="Submit for approval"
                      hint="Send this brand to an admin for review"
                      // `shield`, not `upload`: the logo button one control to the left
                      // uploads a FILE and owns that arrow. Two upload arrows side by side
                      // meaning two different things is the collision rule 13 exists to stop,
                      // and it was on screen until the check learned to read a row as the cell
                      // PLUS the helpers it renders.
                      icon="shield"
                      disabled={busy}
                      data-testid="brand-submit"
                      onClick={() => void run(() => client.submitBrand(b.id), `${b.name} sent for approval`)}
                    />
                  )}
                  {status === "pending" && (
                    <span className="faint" data-testid="brand-pending">Approval pending</span>
                  )}
                  {b.is_default ? (
                    <span className="badge badge--success" data-testid="brand-default-badge">Default</span>
                  ) : (
                    <RowAction
                      label="Make default"
                      hint="Use this brand unless a template says otherwise"
                      icon="star"
                      tone="quiet"
                      disabled={busy}
                      data-testid="brand-make-default"
                      onClick={() => void run(() => client.updateBrand(b.id, { is_default: true }), `${b.name} is now the default`)}
                    />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
